import * as Sentry from '@sentry/react';

import { dispatch } from '../store';
import { ensureFreshTokens, type SessionState } from './cognitoRefresh';
import { hasValidIdToken } from './session';

/**
 * Runs before the app renders: refresh expired ID tokens via Cognito refresh grant,
 * then syncs Redux `isUserAuthenticated`. Only Cognito refusing the refresh
 * signs the user out; a network failure keeps the session.
 *
 * Iframe `signinSilent` is intentionally not used: Cognito Hosted UI CSP blocks
 * framing (`frame-ancestors 'none'`), which causes CSP violations in the console.
 */
export async function bootstrapAuth(): Promise<void> {
  // Never throws. Rendering waits on this, so anything that escaped would take
  // the whole app down rather than the session — the user would get a blank page
  // and no way to tell it from a slow one. A session we could not establish
  // simply means the app comes up signed out.
  let state: SessionState = 'signed_out';
  try {
    state = await ensureFreshTokens();
  } catch (error) {
    Sentry.captureMessage('auth bootstrap failed; starting signed out', {
      level: 'warning',
      extra: { error: String(error) },
    });
  }
  // Offline at load keeps the user in the app with their (expired) tokens: the
  // first API call's 401 retries the refresh, and a network failure there is
  // shown as an error rather than a sign-out.
  dispatch.auth.setUserAuthenticated(hasValidIdToken() || state === 'offline');
}
